import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Modal,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import apiClient from "../../../api/apiClient";
import { useTranslation } from "react-i18next";

import {
  FaRobot,
  FaTimes,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaSync,
  FaChevronDown,
  FaChevronUp,
  FaStar,
  FaRegStar,
  FaStickyNote,
  FaSave,
  FaPlus,
} from "react-icons/fa";
import { Icon } from "../../../components/common/Icon";
import aiChatbotsService, { ConversationAnnotation, ConversationTag } from "../../../features/ai_chatbots/aiChatbotsService";

interface Message {
  id: number;
  conversation_id: number;
  ai_chatbot_id: number;
  role: string;
  content: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface AIChatbotConversationModalProps {
  show: boolean;
  onHide: () => void;
  conversationId: number | null;
  sessionId: string;
  chatbotId: number;
  chatbotName: string;
}

const PAGE_SIZE = 20;
const SEARCH_LIMIT = 1000;

const AIChatbotConversationModal: React.FC<AIChatbotConversationModalProps> = ({
  show,
  onHide,
  conversationId,
  sessionId,
  chatbotId,
  chatbotName,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const scrollRestoreRef = useRef<{ oldScrollHeight: number; oldScrollTop: number } | null>(null);
  const initialLoadDoneRef = useRef(false);
  const filterEffectRan = useRef(false);

  // Annotation state
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [annotation, setAnnotation] = useState<ConversationAnnotation | null>(null);
  const [annotationNote, setAnnotationNote] = useState("");
  const [annotationIsFavorite, setAnnotationIsFavorite] = useState(false);
  const [annotationTagIds, setAnnotationTagIds] = useState<number[]>([]);
  const [availableTags, setAvailableTags] = useState<ConversationTag[]>([]);
  const [annotationSaving, setAnnotationSaving] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#6366f1");
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [tagSectionExpanded, setTagSectionExpanded] = useState(false);
  const [chatbotCompanyId, setChatbotCompanyId] = useState<number | null>(null);

  const loadAnnotation = useCallback(async () => {
    if (!conversationId) return;
    try {
      const ann = await aiChatbotsService.getConversationAnnotation(conversationId);
      setAnnotation(ann);
      setAnnotationNote("");
      setAnnotationIsFavorite(ann.is_favorite);
      setAnnotationTagIds(ann.tags?.map(t => t.id) || []);
    } catch {
      setAnnotation(null);
      setAnnotationNote("");
      setAnnotationIsFavorite(false);
      setAnnotationTagIds([]);
    }
  }, [conversationId]);

  const loadTags = useCallback(async (companyId?: number | null) => {
    try {
      const params: { limit: number; company_id?: number } = { limit: 200 };
      if (companyId != null) params.company_id = companyId;
      const response = await aiChatbotsService.getConversationTags(params);
      const tagData = (response as any).data || response || [];
      setAvailableTags(Array.isArray(tagData) ? tagData : []);
    } catch {
      setAvailableTags([]);
    }
  }, []);

  const handleSaveAnnotation = useCallback(async () => {
    if (!conversationId) return;
    setAnnotationSaving(true);
    try {
      const result = await aiChatbotsService.upsertConversationAnnotation(conversationId, {
        note: annotationNote.trim() || undefined,
        is_favorite: annotationIsFavorite,
        tag_ids: annotationTagIds,
      });
      setAnnotation(result);
      setAnnotationNote("");
      setAnnotationIsFavorite(result.is_favorite);
      setAnnotationTagIds(result.tags?.map(t => t.id) || []);
    } catch (err) {
      console.error("Error saving annotation:", err);
    } finally {
      setAnnotationSaving(false);
    }
  }, [conversationId, annotationNote, annotationIsFavorite, annotationTagIds]);

  const handleToggleFavoriteModal = useCallback(async () => {
    if (!conversationId) return;
    try {
      const result = await aiChatbotsService.toggleConversationFavorite(conversationId);
      setAnnotation(result);
      setAnnotationIsFavorite(result.is_favorite);
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  }, [conversationId]);

  const handleCreateTag = useCallback(async () => {
    if (!newTagName.trim()) return;
    let companyId = chatbotCompanyId;
    if (companyId == null && chatbotId) {
      try {
        const chatbot = await aiChatbotsService.getChatbot(chatbotId);
        companyId = chatbot.company_id;
        setChatbotCompanyId(companyId);
      } catch (e) {
        console.error("Error loading chatbot for company:", e);
        return;
      }
    }
    if (companyId == null) return;
    try {
      const tag = await aiChatbotsService.createConversationTag({
        name: newTagName.trim(),
        color: newTagColor,
        company_id: companyId,
      });
      setAvailableTags(prev => [...prev, tag]);
      setAnnotationTagIds(prev => [...prev, tag.id]);
      setNewTagName("");
      setShowNewTagForm(false);
    } catch (err) {
      console.error("Error creating tag:", err);
    }
  }, [newTagName, newTagColor, chatbotCompanyId, chatbotId]);

  const handleTagToggle = useCallback((tagId: number) => {
    setAnnotationTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  }, []);

  useEffect(() => {
    if (show && conversationId) {
      initialLoadDoneRef.current = false;
      fetchMessages(true);
      filterEffectRan.current = false;
      loadAnnotation();
      if (!chatbotId) loadTags();
    }
  }, [show, conversationId, chatbotId, loadTags]);

  useEffect(() => {
    if (!show || !chatbotId) {
      if (!show) setChatbotCompanyId(null);
      return;
    }
    let cancelled = false;
    aiChatbotsService
      .getChatbot(chatbotId)
      .then(chatbot => {
        if (!cancelled) setChatbotCompanyId(chatbot.company_id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [show, chatbotId]);

  useEffect(() => {
    if (show && chatbotCompanyId != null) loadTags(chatbotCompanyId);
  }, [show, chatbotCompanyId, loadTags]);

  useEffect(() => {
    if (!show || !conversationId) return;
    if (!filterEffectRan.current) {
      filterEffectRan.current = true;
      return;
    }
    fetchMessages(true);
  }, [searchTerm, dateFilter, customStartDate, customEndDate]);

  useEffect(() => {
    setFilteredMessages(messages);
  }, [messages]);

  // Scroll restore: yukarı kaydırıp load more sonrası aynı yerde kal (en aşağıya inme)
  useEffect(() => {
    const restore = scrollRestoreRef.current;
    if (!restore || !messagesAreaRef.current) return;
    scrollRestoreRef.current = null;
    const container = messagesAreaRef.current;
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        const delta = newScrollHeight - restore.oldScrollHeight;
        if (delta > 0) {
          container.scrollTop = restore.oldScrollTop + delta;
        }
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [filteredMessages]);

  // Sadece ilk açılışta (ilk mesaj listesi geldikten sonra) bir kez en alta in; arada aşağı kaydırıp yukarı çıksan bile load more sonrası en alta inme
  useEffect(() => {
    if (filteredMessages.length === 0 || searchTerm.trim() !== "" || dateFilter !== "all") return;
    if (initialLoadDoneRef.current) return; // zaten ilk scroll yapıldı veya load more sonrası
    initialLoadDoneRef.current = true;
    const el = messagesEndRef.current;
    if (!el) return;
    const rafId = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "auto", block: "end" });
    });
    return () => cancelAnimationFrame(rafId);
  }, [filteredMessages.length, searchTerm, dateFilter]);

  const getDateParams = (): { start_date?: string; end_date?: string } => {
    if (dateFilter === "custom" && customStartDate && customEndDate) {
      return { start_date: customStartDate, end_date: customEndDate };
    }
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    if (dateFilter === "today") return { start_date: today.toISOString().slice(0, 10), end_date: todayEnd.toISOString().slice(0, 10) };
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateFilter === "yesterday") return { start_date: yesterday.toISOString().slice(0, 10), end_date: yesterday.toISOString().slice(0, 10) };
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    if (dateFilter === "lastWeek") return { start_date: lastWeek.toISOString().slice(0, 10), end_date: todayEnd.toISOString().slice(0, 10) };
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    if (dateFilter === "lastMonth") return { start_date: lastMonth.toISOString().slice(0, 10), end_date: todayEnd.toISOString().slice(0, 10) };
    return {};
  };

  const hasActiveFilter = searchTerm.trim() !== "" || dateFilter !== "all";

  const fetchMessages = async (reset: boolean = true) => {
    if (!conversationId) return;

    if (reset) {
      setLoading(true);
      setMessages([]);
      setHasMore(true);
    }
    setError(null);
    try {
      const limit = hasActiveFilter ? SEARCH_LIMIT : PAGE_SIZE;
      const skip = reset ? 0 : messages.length;
      const params: Record<string, string | number> = {
        skip,
        limit,
        ...(sessionId ? { session_id: sessionId } : {}),
        ...(searchTerm.trim() ? { text_search: searchTerm.trim() } : {}),
        ...getDateParams(),
      };
      const response = await apiClient.get(`/ai-chatbots/${conversationId}/messages`, { params });
      const raw = (response as any).data ?? response;
      const list = Array.isArray(raw)
        ? raw.sort(
            (a: Message, b: Message) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          )
        : [];
      if (reset) {
        setMessages(list);
        setHasMore(!hasActiveFilter && list.length === PAGE_SIZE);
      } else {
        setMessages((prev) => {
          const merged = [...list, ...prev];
          merged.sort(
            (a, b) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          return merged;
        });
        setHasMore(list.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Failed to load conversation messages. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = async () => {
    if (!conversationId || loadingMore || !hasMore || hasActiveFilter) return;
    setLoadingMore(true);
    const container = messagesAreaRef.current;
    if (container) {
      scrollRestoreRef.current = {
        oldScrollHeight: container.scrollHeight,
        oldScrollTop: container.scrollTop,
      };
    }
    try {
      const params: Record<string, string | number> = {
        skip: messages.length,
        limit: PAGE_SIZE,
        ...(sessionId ? { session_id: sessionId } : {}),
      };
      const response = await apiClient.get(`/ai-chatbots/${conversationId}/messages`, { params });
      const raw = (response as any).data ?? response;
      const list = Array.isArray(raw)
        ? raw.sort(
            (a: Message, b: Message) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          )
        : [];
      setMessages((prev) => {
        const merged = [...list, ...prev];
        merged.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        return merged;
      });
      setHasMore(list.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error loading more messages:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = () => {
    const el = messagesAreaRef.current;
    if (!el || hasActiveFilter || !hasMore || loadingMore) return;
    if (el.scrollTop < 100) {
      loadMore();
    }
  };

  useEffect(() => {
    setFilteredMessages(messages);
  }, [messages]);

  const clearFilters = () => {
    setSearchTerm("");
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setShowCustomDatePicker(false);
  };

  const handleDateFilterChange = (filter: string) => {
    setDateFilter(filter);
    if (filter === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString();
    }
  };

  const shouldShowDateSeparator = (
    currentMessage: Message,
    previousMessage: Message | null
  ) => {
    if (!previousMessage) return true;

    const currentDate = new Date(currentMessage.created_at).toDateString();
    const previousDate = new Date(previousMessage.created_at).toDateString();

    return currentDate !== previousDate;
  };

  const renderMessageContent = (content: string) => {
    // If no search term, just render markdown
    if (!searchTerm.trim()) {
      return <ReactMarkdown>{content}</ReactMarkdown>;
    }

    // For search highlighting with markdown, we need to parse markdown first
    // then highlight. This is a simplified approach: render markdown and then
    // apply highlighting on the rendered HTML structure
    // Note: This is a basic implementation. For complex cases, consider using
    // a more sophisticated approach with markdown AST manipulation

    // Simple approach: highlight in markdown source, then render
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi");
    const highlightedContent = content.replace(regex, (match) => `**${match}**`);

    return <ReactMarkdown>{highlightedContent}</ReactMarkdown>;
  };

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case "all":
        return "All Time";
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "lastWeek":
        return "Last Week";
      case "lastMonth":
        return "Last Month";
      case "custom":
        if (customStartDate && customEndDate) {
          return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
        }
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        className="premium-conversation-modal"
        backdrop="static"
      >
        <div className="modal-wrapper">
          {/* Premium Header */}
          <div className="premium-header">
            <div className="header-left">
              <div className="chatbot-avatar-premium">
                <div className="avatar-inner">
                  <Icon icon={FaRobot} />
                </div>
                <div className="status-indicator-premium"></div>
              </div>
              <div className="chatbot-info">
                <h6 className="chatbot-title">#{chatbotName}</h6>
                <div className="session-details">
                  <span className="session-badge">{sessionId}</span>
                </div>
              </div>
            </div>
            <div className="header-actions">
              <button
                className={`action-btn-premium fav-header-btn ${annotationIsFavorite ? "active-fav" : ""}`}
                onClick={handleToggleFavoriteModal}
                title={annotationIsFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <Icon icon={annotationIsFavorite ? FaStar : FaRegStar} />
              </button>
              <button
                className={`action-btn-premium annotation-btn ${showAnnotationPanel ? "active" : ""}`}
                onClick={() => setShowAnnotationPanel(true)}
                title={t("notes_and_tags")}
              >
                <Icon icon={FaStickyNote} />
              </button>
              <button
                className="action-btn-premium refresh-btn"
                onClick={() => fetchMessages(true)}
                disabled={loading}
                title="Refresh messages"
              >
                <Icon icon={FaSync} />
              </button>
              <button
                className={`action-btn-premium filter-btn ${showFilters ? "active" : ""}`}
                onClick={() => setShowFilters(!showFilters)}
                title="Toggle filters"
              >
                <Icon icon={FaFilter} />
              </button>
              <button className="action-btn-premium close-btn" onClick={onHide}>
                <Icon icon={FaTimes} />
              </button>
            </div>
          </div>

          {/* Premium Filter Bar */}
          {showFilters && (
            <div className="premium-filter-bar">
              <div className="filter-row">
                <div className="search-section-premium">
                  <div className="search-input-wrapper">
                    <Icon icon={FaSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search in conversation..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input-premium"
                    />
                    {searchTerm && (
                      <button
                        className="clear-search-btn"
                        onClick={() => setSearchTerm("")}
                      >
                        <Icon icon={FaTimes} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="date-filter-section-premium">
                  <Dropdown>
                    <Dropdown.Toggle as="div" className="date-filter-toggle">
                      <Icon icon={FaCalendarAlt} />
                      <span>{getDateFilterLabel()}</span>
                      <Icon icon={FaChevronDown} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="date-filter-menu">
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("all")}
                      >
                        All Time
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("today")}
                      >
                        Today
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("yesterday")}
                      >
                        Yesterday
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("lastWeek")}
                      >
                        Last Week
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("lastMonth")}
                      >
                        Last Month
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("custom")}
                      >
                        Custom Range
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {(searchTerm || dateFilter !== "all") && (
                  <button
                    className="clear-filters-btn-premium"
                    onClick={clearFilters}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Custom Date Range Picker */}
              {showCustomDatePicker && (
                <div className="custom-date-picker">
                  <div className="date-inputs">
                    <div className="date-input-group">
                      <label>From</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="date-input-premium"
                      />
                    </div>
                    <div className="date-separator-line"></div>
                    <div className="date-input-group">
                      <label>To</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="date-input-premium"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Premium Messages Area */}
          <div
            ref={messagesAreaRef}
            className="premium-messages-area"
            onScroll={handleScroll}
          >
            {loading ? (
              <div className="state-container-premium loading-state">
                <div className="state-content-premium">
                  <div className="loading-animation">
                    <div className="loading-dots">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                  <h6>Loading Messages</h6>
                  <p>Fetching your conversation history...</p>
                </div>
              </div>
            ) : error ? (
              <div className="state-container-premium error-state">
                <div className="state-content-premium">
                  <div className="error-icon-premium">⚠️</div>
                  <h6>Connection Error</h6>
                  <p>{error}</p>
                  <button className="retry-btn-premium" onClick={() => fetchMessages(true)}>
                    <Icon icon={FaSync} /> Retry
                  </button>
                </div>
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="state-container-premium empty-state">
                <div className="state-content-premium">
                  <div className="empty-icon-premium">
                    {searchTerm || dateFilter !== "all" ? "🔍" : "💬"}
                  </div>
                  <h6>
                    {searchTerm || dateFilter !== "all"
                      ? "No Results Found"
                      : "No Messages Yet"}
                  </h6>
                  <p>
                    {searchTerm || dateFilter !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "Start a conversation to see messages here."}
                  </p>
                  {(searchTerm || dateFilter !== "all") && (
                    <button
                      className="clear-filters-btn-premium"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="messages-list-premium">
                {loadingMore && (
                  <div className="load-more-indicator">
                    <Spinner animation="border" size="sm" /> Loading older messages...
                  </div>
                )}
                {filteredMessages.map((message, index) => {
                  const previousMessage =
                    index > 0 ? filteredMessages[index - 1] : null;
                  const showDateSeparator = shouldShowDateSeparator(
                    message,
                    previousMessage
                  );

                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <div className="date-separator-premium">
                          <span className="date-text-premium">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                      )}

                      <div
                        className={`message-row-premium ${message.role === "user" ? "user-row" : "bot-row"}`}
                      >
                        <div className="message-wrapper-premium">
                          <div className="message-bubble-premium">
                            <div className="message-content-premium">
                              {message.role === "assistant" ? (
                                renderMessageContent(message.content)
                              ) : (
                                message.content
                              )}
                            </div>
                            <div className="message-meta-premium">
                              <span className="message-time-premium">
                                {formatTime(message.created_at)}
                              </span>
                              {message.status === "failed" && (
                                <span className="message-status-failed">
                                  Failed
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Premium Footer */}
          <div className="premium-footer">
            <div className="footer-info-premium">
              <div className="message-count-premium">
                <span className="count-number">
                  {filteredMessages.length !== messages.length
                    ? `${filteredMessages.length} of ${messages.length}`
                    : `${messages.length}`}
                </span>
                <span className="count-label">messages</span>
              </div>
              {(searchTerm || dateFilter !== "all") && (
                <div className="filter-indicator-premium">
                  <Icon icon={FaFilter} />
                  Filtered
                </div>
              )}
            </div>
            <button className="close-modal-btn-premium" onClick={onHide}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Notes & Tags - premium standalone modal */}
      <Modal
        show={showAnnotationPanel}
        onHide={() => setShowAnnotationPanel(false)}
        centered
        size="lg"
        className="notes-tags-modal"
        backdrop="static"
      >
        <div className="nt-modal-inner">
          <div className="nt-header">
            <div className="nt-header-content">
              <div className="nt-icon">
                <Icon icon={FaStickyNote} />
              </div>
              <h2 className="nt-title">{t("notes_and_tags")}</h2>
            </div>
            <button
              type="button"
              className="nt-close"
              onClick={() => setShowAnnotationPanel(false)}
              aria-label="Close"
            >
              <Icon icon={FaTimes} />
            </button>
          </div>

          <div className="nt-body">
            <div className="nt-block">
              <label className="nt-label">{t("note")}</label>
              {annotation?.notes && annotation.notes.length > 0 && (
                <div className="nt-notes-history">
                  {annotation.notes.map((n) => (
                    <div key={n.id} className="nt-note-item">
                      <div className="nt-note-meta">
                        <span className="nt-note-author">{n.user_name}</span>
                        <span className="nt-note-date">
                          {n.created_at ? new Date(n.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : ""}
                        </span>
                      </div>
                      <div className="nt-note-text">{n.note_text}</div>
                    </div>
                  ))}
                </div>
              )}
              <textarea
                className="nt-textarea"
                placeholder={t("annotation_note_placeholder")}
                value={annotationNote}
                onChange={(e) => setAnnotationNote(e.target.value)}
                rows={3}
              />
            </div>

            <div className="nt-divider" />

            <div className="nt-block">
              <div className="nt-block-head">
                <label className="nt-label">{t("tags")}</label>
                <button
                  type="button"
                  className="nt-btn-add"
                  onClick={() => setShowNewTagForm(!showNewTagForm)}
                >
                  <Icon icon={FaPlus} />
                  <span>{t("new")}</span>
                </button>
              </div>

              {showNewTagForm && (
                <div className="nt-new-tag">
                  <input
                    type="text"
                    className="nt-new-tag-input"
                    placeholder={t("tag_name_placeholder")}
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleCreateTag()}
                    autoFocus
                  />
                  <div className="nt-color-wrap">
                    <input
                      type="color"
                      className="nt-color-input"
                      value={newTagColor}
                      onChange={(e) => setNewTagColor(e.target.value)}
                    />
                    <span className="nt-color-preview" style={{ background: newTagColor }} />
                  </div>
                  <button
                    type="button"
                    className="nt-btn-create"
                    onClick={handleCreateTag}
                    disabled={!newTagName.trim()}
                  >
                    {t("create")}
                  </button>
                </div>
              )}

              {availableTags.length > 0 && (
                <div className="nt-search-wrap">
                  <Icon icon={FaSearch} className="nt-search-icon" />
                  <input
                    type="text"
                    className="nt-search-input"
                    placeholder={t("search_tags")}
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    aria-label={t("search_tags")}
                  />
                </div>
              )}

              <div className="nt-tags">
                {availableTags.length === 0 ? (
                  <p className="nt-empty">{t("no_tags_yet")}</p>
                ) : (
                  (() => {
                    const q = tagSearch.trim().toLowerCase();
                    const filtered = q
                      ? availableTags.filter((tag) => tag.name.toLowerCase().includes(q))
                      : availableTags;
                    const visibleCount = 15;
                    const visible = tagSectionExpanded ? filtered : filtered.slice(0, visibleCount);
                    const hasMore = filtered.length > visibleCount;
                    if (filtered.length === 0) {
                      return <p className="nt-empty">{t("no_matching_tags")}</p>;
                    }
                    return (
                      <>
                        {visible.map((tag) => {
                          const isSelected = annotationTagIds.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              className={`nt-tag ${isSelected ? "selected" : ""}`}
                              style={{
                                ["--nt-c" as string]: tag.color,
                              } as React.CSSProperties}
                              onClick={() => handleTagToggle(tag.id)}
                            >
                              <span className="nt-tag-dot" style={{ background: tag.color }} />
                              {tag.name}
                              {isSelected && <span className="nt-tag-check">&#10003;</span>}
                            </button>
                          );
                        })}
                        {hasMore && (
                          <button
                            type="button"
                            className="nt-show-more"
                            onClick={() => setTagSectionExpanded((v) => !v)}
                          >
                            {tagSectionExpanded ? (
                              <><Icon icon={FaChevronUp} /> {t("show_less")}</>
                            ) : (
                              <><Icon icon={FaChevronDown} /> {t("show_more")} (+{filtered.length - visibleCount})</>
                            )}
                          </button>
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            </div>
          </div>

          <div className="nt-footer">
            <button
              type="button"
              className="nt-save"
              onClick={async () => {
                await handleSaveAnnotation();
                setShowAnnotationPanel(false);
              }}
              disabled={annotationSaving}
            >
              {annotationSaving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Icon icon={FaSave} />
              )}
              <span>{annotationSaving ? t("saving") : t("save_changes")}</span>
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        .premium-conversation-modal {
          --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --secondary-gradient: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          --success-gradient: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          --danger-gradient: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
          --dark-gradient: linear-gradient(135deg, #434343 0%, #000000 100%);
          
          --primary-purple: #667eea;
          --primary-purple-light: #8b9cf7;
          --primary-purple-dark: #5a6fd8;
          --accent-pink: #f093fb;
          --accent-blue: #4facfe;
          --success-green: #00f2fe;
          --danger-red: #fa709a;
          --warning-orange: #fee140;
          
          --neutral-50: #fafafa;
          --neutral-100: #f5f5f5;
          --neutral-200: #e5e5e5;
          --neutral-300: #d4d4d4;
          --neutral-400: #a3a3a3;
          --neutral-500: #737373;
          --neutral-600: #525252;
          --neutral-700: #404040;
          --neutral-800: #262626;
          --neutral-900: #171717;
          
          --glass-bg: rgba(255, 255, 255, 0.95);
          --glass-border: rgba(255, 255, 255, 0.2);
          --backdrop-blur: blur(20px);
          
          --shadow-xs: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
          --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
          
          --border-radius-sm: 6px;
          --border-radius: 12px;
          --border-radius-lg: 20px;
          --border-radius-xl: 24px;
          --border-radius-full: 9999px;
          
          --transition-fast: all 0.15s ease;
          --transition-normal: all 0.3s ease;
          --transition-slow: all 0.5s ease;
        }

        /* Stack above Messages modal when opened from it */
        .premium-conversation-modal.modal {
          z-index: 1060 !important;
        }

        .premium-conversation-modal .modal-dialog {
          max-width: 950px;
          margin: 1rem auto;
        }

        .premium-conversation-modal .modal-content {
          border: none;
          border-radius: var(--border-radius-xl);
          box-shadow: var(--shadow-2xl);
          overflow: hidden;
          height: 88vh;
          max-height: 800px;
          background: var(--glass-bg);
          backdrop-filter: var(--backdrop-blur);
        }

        .modal-wrapper {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: transparent;
          min-height: 0;
        }

        /* Premium Header */
        .premium-header {
          background: var(--primary-gradient);
          color: white;
          padding: 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          overflow: hidden;
        }

        .premium-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.08)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          opacity: 0.3;
          pointer-events: none;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          position: relative;
          z-index: 1;
        }

        .chatbot-avatar-premium {
          position: relative;
          width: 60px;
          height: 60px;
        }

        .avatar-inner {
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.2);
          border-radius: var(--border-radius-full);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          backdrop-filter: var(--backdrop-blur);
          border: 2px solid rgba(255, 255, 255, 0.3);
          transition: var(--transition-normal);
        }

        .avatar-inner:hover {
          transform: scale(1.05);
          background: rgba(255, 255, 255, 0.3);
        }

        .status-indicator-premium {
          position: absolute;
          bottom: 4px;
          right: 4px;
          width: 16px;
          height: 16px;
          background: var(--success-gradient);
          border-radius: var(--border-radius-full);
          border: 3px solid white;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .chatbot-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .chatbot-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .session-details {
          display: flex;
          align-items: center;
        }

        .session-badge {
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
          background: rgba(255, 255, 255, 0.2);
          padding: 0.375rem 0.75rem;
          border-radius: var(--border-radius-full);
          font-size: 0.8rem;
          font-weight: 600;
          backdrop-filter: var(--backdrop-blur);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          position: relative;
          z-index: 1;
        }

        .action-btn-premium {
          background: rgba(255, 255, 255, 0.15);
          border: none;
          border-radius: var(--border-radius-full);
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          cursor: pointer;
          transition: var(--transition-normal);
          backdrop-filter: var(--backdrop-blur);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .action-btn-premium:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .action-btn-premium:active {
          transform: translateY(0);
        }

        .action-btn-premium:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .action-btn-premium.active {
          background: rgba(255, 255, 255, 0.3);
          box-shadow: var(--shadow-md);
        }

        .refresh-btn svg {
          animation: ${loading ? "spin 1s linear infinite" : "none"};
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Premium Filter Bar */
        .premium-filter-bar {
          background: var(--neutral-50);
          border-bottom: 1px solid var(--neutral-200);
          padding: 1.5rem;
        }

        .filter-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .search-section-premium {
          flex: 1;
          min-width: 250px;
        }

        .search-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: var(--neutral-400);
          z-index: 1;
        }

        .search-input-premium {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          border: 2px solid var(--neutral-200);
          border-radius: var(--border-radius-full);
          font-size: 0.95rem;
          background: white;
          transition: var(--transition-normal);
          outline: none;
        }

        .search-input-premium:focus {
          border-color: var(--primary-purple);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .clear-search-btn {
          position: absolute;
          right: 0.75rem;
          background: var(--neutral-200);
          border: none;
          border-radius: var(--border-radius-full);
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: var(--transition-fast);
        }

        .clear-search-btn:hover {
          background: var(--neutral-300);
        }

        .date-filter-section-premium {
          flex-shrink: 0;
        }

        .date-filter-toggle {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.25rem;
          background: white;
          border: 2px solid var(--neutral-200);
          border-radius: var(--border-radius-full);
          cursor: pointer;
          transition: var(--transition-normal);
          font-weight: 500;
          color: var(--neutral-700);
          min-width: 180px;
          justify-content: space-between;
        }

        .date-filter-toggle:hover {
          border-color: var(--primary-purple);
          box-shadow: var(--shadow-sm);
        }

        .date-filter-menu {
          border-radius: var(--border-radius);
          border: none;
          box-shadow: var(--shadow-xl);
          padding: 0.5rem;
        }

        .date-filter-menu .dropdown-item {
          border-radius: var(--border-radius-sm);
          padding: 0.75rem 1rem;
          transition: var(--transition-fast);
        }

        .date-filter-menu .dropdown-item:hover {
          background: var(--primary-purple);
          color: white;
        }

        .clear-filters-btn-premium {
          background: var(--danger-gradient);
          border: none;
          border-radius: var(--border-radius-full);
          padding: 0.875rem 1.5rem;
          color: white;
          font-weight: 600;
                    cursor: pointer;
          transition: var(--transition-normal);
          box-shadow: var(--shadow-sm);
        }

        .clear-filters-btn-premium:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        /* Custom Date Range Picker */
        .custom-date-picker {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--neutral-200);
        }

        .date-inputs {
          display: flex;
          align-items: center;
          gap: 1rem;
          justify-content: center;
        }

        .date-input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .date-input-group label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--neutral-600);
        }

        .date-input-premium {
          padding: 0.75rem 1rem;
          border: 2px solid var(--neutral-200);
          border-radius: var(--border-radius);
          font-size: 0.9rem;
          background: white;
          transition: var(--transition-normal);
          outline: none;
          min-width: 150px;
        }

        .date-input-premium:focus {
          border-color: var(--primary-purple);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .date-separator-line {
          width: 20px;
          height: 2px;
          background: var(--neutral-300);
          border-radius: 1px;
          margin-top: 1.5rem;
        }

        /* Premium Messages Area */
        .premium-messages-area {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          background: linear-gradient(180deg, var(--neutral-50) 0%, var(--neutral-100) 100%);
          position: relative;
        }

        /* State Containers */
        .state-container-premium {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 3rem;
        }

        .state-content-premium {
          text-align: center;
          max-width: 450px;
        }

        /* Loading State */
        .loading-animation {
          margin-bottom: 2rem;
        }

        .loading-dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
        }

        .loading-dots div {
          width: 12px;
          height: 12px;
          background: var(--primary-gradient);
          border-radius: var(--border-radius-full);
          animation: loadingBounce 1.4s ease-in-out infinite both;
        }

        .loading-dots div:nth-child(1) { animation-delay: -0.32s; }
        .loading-dots div:nth-child(2) { animation-delay: -0.16s; }

        @keyframes loadingBounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        .loading-state h6 {
          margin: 0 0 0.75rem;
          color: var(--primary-purple);
          font-weight: 700;
          font-size: 1.1rem;
        }

        .loading-state p {
          color: var(--neutral-500);
          margin: 0;
          font-size: 0.95rem;
        }

        /* Error State */
        .error-icon-premium {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
        }

        .error-state h6 {
          color: var(--danger-red);
          margin-bottom: 0.75rem;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .error-state p {
          color: var(--neutral-500);
          margin-bottom: 2rem;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .retry-btn-premium {
          background: var(--danger-gradient);
          border: none;
          border-radius: var(--border-radius-full);
          padding: 0.875rem 2rem;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition-normal);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: var(--shadow-sm);
        }

        .retry-btn-premium:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        /* Empty State */
        .empty-icon-premium {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
        }

        .empty-state h6 {
          color: var(--neutral-700);
          margin-bottom: 0.75rem;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .empty-state p {
          color: var(--neutral-500);
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        /* Messages List */
        .messages-list-premium {
          padding: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .load-more-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem;
          color: var(--neutral-500);
          font-size: 0.9rem;
        }

        /* Date Separator */
        .date-separator-premium {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 2rem 0;
          position: relative;
        }

        .date-separator-premium::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, var(--neutral-300) 20%, var(--neutral-300) 80%, transparent 100%);
        }

        .date-text-premium {
          background: white;
          padding: 0.625rem 1.25rem;
          border-radius: var(--border-radius-full);
          font-size: 0.85rem;
          color: var(--neutral-600);
          font-weight: 600;
          border: 2px solid var(--neutral-200);
          position: relative;
          z-index: 1;
          box-shadow: var(--shadow-xs);
        }

        /* Message Rows */
        .message-row-premium {
          display: flex;
          margin-bottom: 0.75rem;
        }

        .user-row {
          justify-content: flex-end;
        }

        .bot-row {
          justify-content: flex-start;
        }

        .message-wrapper-premium {
          max-width: 75%;
          display: flex;
          flex-direction: column;
        }

        /* Message Bubbles */
        .message-bubble-premium {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
          animation: messageSlideIn 0.4s ease-out;
        }

        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(15px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .message-content-premium {
          padding: 1rem 1.375rem;
          border-radius: var(--border-radius-lg);
          font-size: 0.95rem;
          line-height: 1.6;
          word-wrap: break-word;
          white-space: pre-wrap;
          box-shadow: var(--shadow-md);
          position: relative;
          overflow: hidden;
        }

        .user-row .message-content-premium {
          background: var(--primary-gradient);
          color: white;
          border-bottom-right-radius: 8px;
        }

        .user-row .message-content-premium::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
          pointer-events: none;
        }

        .bot-row .message-content-premium {
          background: white;
          color: var(--neutral-800);
          border-bottom-left-radius: 8px;
          border: 2px solid var(--neutral-200);
        }

        .message-meta-premium {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 0 0.75rem;
          margin-top: 0.375rem;
        }

        .message-time-premium {
          font-size: 0.8rem;
          color: var(--neutral-400);
          font-weight: 500;
        }

        .message-status-failed {
          font-size: 0.75rem;
          color: var(--danger-red);
          font-weight: 600;
          background: rgba(250, 112, 154, 0.1);
          padding: 0.25rem 0.5rem;
          border-radius: var(--border-radius-full);
        }

        /* Search Highlight */
        .search-highlight {
          background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-weight: 700;
          color: var(--neutral-800);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        /* Premium Footer */
        .premium-footer {
          background: white;
          border-top: 2px solid var(--neutral-200);
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          backdrop-filter: var(--backdrop-blur);
        }

        .footer-info-premium {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .message-count-premium {
          display: flex;
          align-items: baseline;
          gap: 0.5rem;
        }

        .count-number {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--primary-purple);
        }

        .count-label {
          font-size: 0.9rem;
          color: var(--neutral-500);
          font-weight: 500;
        }

        .filter-indicator-premium {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: var(--primary-purple);
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
          padding: 0.5rem 1rem;
          border-radius: var(--border-radius-full);
          font-weight: 600;
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        .close-modal-btn-premium {
          background: var(--neutral-600);
          border: none;
          border-radius: var(--border-radius);
          padding: 0.875rem 2rem;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition-normal);
          box-shadow: var(--shadow-sm);
        }

        .close-modal-btn-premium:hover {
          background: var(--neutral-700);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        /* Scrollbar Styling */
        .premium-messages-area::-webkit-scrollbar {
          width: 8px;
        }

        .premium-messages-area::-webkit-scrollbar-track {
          background: var(--neutral-100);
          border-radius: 4px;
        }

        .premium-messages-area::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, var(--neutral-300) 0%, var(--neutral-400) 100%);
          border-radius: 4px;
        }

        .premium-messages-area::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, var(--neutral-400) 0%, var(--neutral-500) 100%);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .premium-conversation-modal .modal-dialog {
            margin: 0.5rem;
            max-width: none;
          }

          .premium-conversation-modal .modal-content {
            height: 95vh;
            border-radius: var(--border-radius-lg);
          }

          .premium-header {
            padding: 1.5rem;
          }

          .premium-filter-bar {
            padding: 1rem;
          }

          .filter-row {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .search-section-premium {
            min-width: auto;
          }

          .date-filter-toggle {
            min-width: auto;
          }

          .chatbot-avatar-premium {
            width: 50px;
            height: 50px;
          }

          .avatar-inner {
            font-size: 20px;
          }

          .chatbot-title {
            font-size: 1.1rem;
          }

          .session-badge {
            font-size: 0.75rem;
            padding: 0.25rem 0.625rem;
          }

          .action-btn-premium {
            width: 40px;
            height: 40px;
          }

          .message-wrapper-premium {
            max-width: 85%;
          }

          .message-content-premium {
            padding: 0.875rem 1.125rem;
            font-size: 0.9rem;
          }

          .messages-list-premium {
            padding: 1.5rem;
          }

          .premium-footer {
            padding: 1rem 1.5rem;
          }

          .date-inputs {
            flex-direction: column;
            gap: 1rem;
          }

          .date-separator-line {
            width: 2px;
            height: 20px;
            margin: 0;
          }
        }

        /* Favorite header button */
        .fav-header-btn.active-fav { background: rgba(245, 166, 35, 0.3); }
        .fav-header-btn.active-fav svg { color: #ffd700; }

        /* Notes & Tags - premium modal (modern, minimal) */
        .notes-tags-modal {
          --nt-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --nt-primary: #667eea;
          --nt-radius: 14px;
          --nt-radius-sm: 10px;
          --nt-radius-full: 9999px;
          --nt-neutral-100: #f5f5f5;
          --nt-neutral-200: #e8e8e8;
          --nt-neutral-400: #a3a3a3;
          --nt-neutral-600: #525252;
          --nt-neutral-800: #262626;
          --nt-shadow: 0 25px 60px rgba(0, 0, 0, 0.12);
          --nt-transition: all 0.2s ease;
        }

        .notes-tags-modal.modal {
          z-index: 1070 !important;
        }

        .notes-tags-modal .modal-dialog {
          max-width: 520px;
          margin: 1.5rem auto;
        }

        .notes-tags-modal .modal-content {
          border: none;
          border-radius: var(--nt-radius);
          box-shadow: var(--nt-shadow);
          overflow: hidden;
        }

        .notes-tags-modal .modal-content .btn-close {
          display: none;
        }

        .nt-modal-inner {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .nt-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 1.5rem;
          background: var(--nt-gradient);
          color: white;
        }

        .nt-header-content {
          display: flex;
          align-items: center;
          gap: 0.875rem;
        }

        .nt-icon {
          width: 42px;
          height: 42px;
          border-radius: var(--nt-radius-sm);
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
        }

        .nt-title {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .nt-close {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: var(--nt-radius-full);
          background: rgba(255, 255, 255, 0.15);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: var(--nt-transition);
        }

        .nt-close:hover {
          background: rgba(255, 255, 255, 0.28);
        }

        .nt-body {
          padding: 1.5rem 1.5rem;
          background: #fafafa;
          overflow: visible;
        }

        .nt-block {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .nt-block + .nt-block {
          margin-top: 0;
        }

        .nt-notes-history {
          margin-bottom: 0.75rem;
          max-height: 200px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .nt-note-item {
          background: var(--nt-neutral-50);
          border: 1px solid var(--nt-border);
          border-radius: 8px;
          padding: 0.5rem 0.75rem;
        }
        .nt-note-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.25rem;
          font-size: 0.7rem;
        }
        .nt-note-author {
          font-weight: 600;
          color: var(--nt-primary);
        }
        .nt-note-date {
          color: var(--nt-neutral-500);
        }
        .nt-note-text {
          font-size: 0.8rem;
          color: var(--nt-text);
          white-space: pre-wrap;
          word-break: break-word;
        }
        .nt-label {
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--nt-neutral-600);
          margin: 0;
        }

        .nt-textarea {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 1px solid var(--nt-neutral-200);
          border-radius: var(--nt-radius-sm);
          font-size: 0.9rem;
          line-height: 1.55;
          resize: none;
          outline: none;
          transition: var(--nt-transition);
          font-family: inherit;
          background: white;
          color: var(--nt-neutral-800);
        }

        .nt-textarea::placeholder {
          color: var(--nt-neutral-400);
        }

        .nt-textarea:focus {
          border-color: var(--nt-primary);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .nt-divider {
          height: 1px;
          background: var(--nt-neutral-200);
          margin: 1.25rem 0;
        }

        .nt-block-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }

        .nt-btn-add {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.4rem 0.85rem;
          border: 1px dashed var(--nt-neutral-400);
          border-radius: var(--nt-radius-full);
          background: transparent;
          color: var(--nt-primary);
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--nt-transition);
        }

        .nt-btn-add:hover {
          border-color: var(--nt-primary);
          background: rgba(102, 126, 234, 0.06);
        }

        .nt-btn-add svg {
          font-size: 0.65rem;
        }

        .nt-new-tag {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          padding: 0.75rem;
          background: white;
          border: 1px solid var(--nt-neutral-200);
          border-radius: var(--nt-radius-sm);
          margin-bottom: 0.75rem;
        }

        .nt-new-tag-input {
          flex: 1;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--nt-neutral-200);
          border-radius: 8px;
          font-size: 0.85rem;
          outline: none;
          transition: var(--nt-transition);
          background: var(--nt-neutral-100);
        }

        .nt-new-tag-input:focus {
          border-color: var(--nt-primary);
          background: white;
        }

        .nt-color-wrap {
          position: relative;
          width: 34px;
          height: 34px;
          flex-shrink: 0;
        }

        .nt-color-input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
          width: 100%;
          height: 100%;
        }

        .nt-color-preview {
          display: block;
          width: 34px;
          height: 34px;
          border-radius: var(--nt-radius-full);
          border: 2px solid white;
          box-shadow: 0 1px 4px rgba(0,0,0,0.1);
          pointer-events: none;
        }

        .nt-btn-create {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 8px;
          background: var(--nt-gradient);
          color: white;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--nt-transition);
          white-space: nowrap;
        }

        .nt-btn-create:hover:not(:disabled) {
          opacity: 0.92;
          transform: translateY(-1px);
        }

        .nt-btn-create:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .nt-search-wrap {
          position: relative;
          margin-bottom: 0.75rem;
        }

        .nt-search-wrap .nt-search-icon {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--nt-neutral-400);
          font-size: 0.75rem;
          pointer-events: none;
        }

        .nt-search-input {
          width: 100%;
          padding: 0.55rem 0.875rem 0.55rem 2.25rem;
          border: 1px solid var(--nt-neutral-200);
          border-radius: var(--nt-radius-full);
          font-size: 0.85rem;
          background: white;
          color: var(--nt-neutral-800);
          outline: none;
          transition: var(--nt-transition);
        }

        .nt-search-input::placeholder {
          color: var(--nt-neutral-400);
        }

        .nt-search-input:focus {
          border-color: var(--nt-primary);
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.08);
        }

        .nt-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          align-items: center;
        }

        .nt-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.4rem 0.85rem;
          border: 1.5px solid var(--nt-c, var(--nt-neutral-200));
          border-radius: var(--nt-radius-full);
          background: rgba(102, 126, 234, 0.08);
          color: var(--nt-c, var(--nt-neutral-600));
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--nt-transition);
        }

        .nt-tag:not(.selected) {
          background: color-mix(in srgb, var(--nt-c, #667eea) 12%, transparent);
        }

        .nt-tag:hover {
          transform: translateY(-1px);
          filter: brightness(0.98);
        }

        .nt-tag.selected {
          background: var(--nt-c, var(--nt-primary));
          color: white;
          border-color: var(--nt-c, var(--nt-primary));
        }

        .nt-tag.selected .nt-tag-dot {
          background: rgba(255,255,255,0.6) !important;
        }

        .nt-tag-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .nt-tag-check {
          font-size: 0.6rem;
          margin-left: 0.05rem;
        }

        .nt-show-more {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.4rem 0.8rem;
          border: 1px dashed var(--nt-neutral-200);
          border-radius: var(--nt-radius-full);
          background: white;
          color: var(--nt-neutral-600);
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--nt-transition);
        }

        .nt-show-more:hover {
          border-color: var(--nt-primary);
          color: var(--nt-primary);
          background: rgba(102, 126, 234, 0.05);
        }

        .nt-show-more svg {
          font-size: 0.65rem;
        }

        .nt-empty {
          margin: 0;
          font-size: 0.85rem;
          color: var(--nt-neutral-400);
          font-style: italic;
        }

        .nt-footer {
          padding: 1rem 1.5rem;
          background: white;
          border-top: 1px solid var(--nt-neutral-200);
          display: flex;
          justify-content: flex-end;
        }

        .nt-save {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 1.5rem;
          border: none;
          border-radius: var(--nt-radius-sm);
          background: var(--nt-gradient);
          color: white;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--nt-transition);
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .nt-save:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
        }

        .nt-save:disabled {
          opacity: 0.7;
          cursor: wait;
          transform: none;
        }

        .nt-save svg {
          font-size: 0.85rem;
        }

        .ann-save-btn svg {
          font-size: 0.8rem;
        }

        /* Scrollbar for drawer */
        @media (max-width: 480px) {
          .premium-conversation-modal .modal-dialog {
            margin: 0.25rem;
          }

          .premium-conversation-modal .modal-content {
            height: 98vh;
            border-radius: var(--border-radius);
          }

          .premium-header {
            padding: 1rem;
          }

          .session-details {
            display: none;
          }

          .message-wrapper-premium {
            max-width: 90%;
          }

          .chatbot-info {
            display: none;
          }

          .header-left {
            gap: 0.75rem;
          }

          .premium-filter-bar {
            padding: 0.75rem;
          }

          .messages-list-premium {
            padding: 1rem;
          }

          .premium-footer {
            padding: 0.75rem 1rem;
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .footer-info-premium {
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
};

export default AIChatbotConversationModal;
